const SENSITIVE_QUERY_KEYS = new Set(['token', 'unsubscribe_token'])

export function isSensitiveAnalyticsPathname(pathname: string): boolean {
  return /^\/unsubscribe(?:\/|$)/.test(pathname)
}

export function isSensitiveAnalyticsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return isSensitiveAnalyticsPathname(url.pathname) || hasSensitiveQuery(url)
  } catch {
    return true
  }
}

function hasSensitiveQuery(url: URL): boolean {
  if (isSensitiveAnalyticsPathname(url.pathname)) {
    return url.search.length > 0 || url.hash.length > 0
  }
  return Array.from(url.searchParams.keys())
    .some((key) => SENSITIVE_QUERY_KEYS.has(key.toLowerCase()))
}

/**
 * Remove bearer-capability query data before an analytics provider sees it.
 * Ordinary campaign parameters remain available; unsubscribe pages and any URL
 * carrying a known token key are reduced to origin + path.
 */
export function redactSensitiveAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value)
    if (hasSensitiveQuery(url)) {
      url.search = ''
      url.hash = ''
    }
    return url.toString()
  } catch {
    // Analytics should fail closed when a provider hands us an invalid URL.
    return 'about:blank'
  }
}

export function redactAnalyticsEventUrl<T extends { url: string }>(event: T): T {
  return { ...event, url: redactSensitiveAnalyticsUrl(event.url) }
}

/**
 * Vercel keeps beforeSend installed after its React component unmounts. Drop,
 * rather than merely redact, a sensitive navigation observed by that persisted
 * callback so the unsubscribe visit itself is not disclosed.
 */
export function filterSensitiveAnalyticsEvent<T extends { url: string }>(
  event: T,
  currentUrl?: string
): T | null {
  if (
    isSensitiveAnalyticsUrl(event.url)
    || (currentUrl !== undefined && isSensitiveAnalyticsUrl(currentUrl))
  ) {
    return null
  }
  return redactAnalyticsEventUrl(event)
}

/**
 * PostHog session replay sends rrweb Meta URLs through the same network-mask
 * callback as performance entries. Redact both field names used across SDK
 * versions so a replay can never retain an unsubscribe capability.
 */
type AnalyticsNetworkRequest = {
  name?: unknown
  url?: unknown
  entryType?: unknown
  startTime?: unknown
  duration?: unknown
  endTime?: unknown
  timeOrigin?: unknown
  timestamp?: unknown
  isInitial?: unknown
  [key: string]: unknown
}

export function redactAnalyticsNetworkRequest(
  request: AnalyticsNetworkRequest,
): AnalyticsNetworkRequest {
  const safe: AnalyticsNetworkRequest = {}

  if (typeof request.name === 'string') {
    safe.name = redactSensitiveAnalyticsUrl(request.name)
  }
  if (typeof request.url === 'string') {
    safe.url = redactSensitiveAnalyticsUrl(request.url)
  }
  if (typeof request.entryType === 'string') safe.entryType = request.entryType
  for (const key of ['startTime', 'duration', 'endTime', 'timeOrigin', 'timestamp']) {
    if (typeof request[key] === 'number') safe[key] = request[key]
  }
  if (typeof request.isInitial === 'boolean') safe.isInitial = request.isInitial

  return safe
}
